import React from 'react';
import classNames from 'classnames';
import { VariantType } from 'src/services';
import { formatSignedPercent, getDeltaClassName } from 'src/utils';
import { ExperimentCardVariantTableProps } from '../types';
import { LiftCell } from '../../LiftCell';

function formatLift(value: number): JSX.Element {
  const text = formatSignedPercent(value);
  return <span className={getDeltaClassName(value, 'lift-positive', 'lift-negative')}>{text}</span>;
}

export const ExperimentCardVariantTable = ({
  experimentItem,
}: ExperimentCardVariantTableProps): JSX.Element => {
  const summaryByExperienceId = new Map(
    (experimentItem.analyticsSummary?.variantSummary ?? []).map(s => [s.experienceId, s])
  );

  const winnerExperienceId =
    [...summaryByExperienceId.values()].find(s => s.isWinner)?.experienceId ?? null;
  const isCollectingData = experimentItem.analyticsSummary?.state === 'collecting_data';

  return (
    <div className='experiment-card-variant-table-wrap'>
      <table className='experiment-card-variant-table'>
        <thead>
          <tr>
            <th className='col-variant'>Variant</th>
            <th className='col-allocation'>Allocation</th>
            <th className='col-metric'>Users</th>
            <th className='col-metric'>Transactions</th>
            <th className='col-metric'>User CVR</th>
            <th className='col-metric'>Lift</th>
          </tr>
        </thead>
        <tbody>
          {experimentItem.variantsDisplayList.map(variant => {
            const isControl = variant.type === VariantType.Control;
            const analytics = summaryByExperienceId.get(variant.id);
            const isWinner = winnerExperienceId === variant.id;
            return (
              <tr
                key={variant.id}
                className={classNames({ 'control-row': isControl, 'winner-row': isWinner })}
              >
                <td className='col-variant'>
                  <div className='variant-name-row'>
                    <span className='variant-name'>{variant.name}</span>
                    {isControl && <span className='control-badge'>CONTROL</span>}
                    {isWinner && (
                      <span className='winner-icon'>
                        <span aria-hidden='true'>🏆</span>
                        <span className='sr-only'>Winner</span>
                      </span>
                    )}
                  </div>
                  <div className='variant-subtitle'>
                    {isControl
                      ? 'Will be used as the baseline (control)'
                      : 'Will be used as the test group'}
                  </div>
                </td>
                <td className='col-allocation'>{variant.trafficAllocation}%</td>
                <td className='col-metric'>{analytics ? analytics.users.toLocaleString() : '—'}</td>
                <td className='col-metric'>
                  {analytics ? analytics.transactions.toLocaleString() : '—'}
                </td>
                <td className='col-metric'>
                  {analytics ? `${(analytics.userCvr * 100).toFixed(2)}%` : '—'}
                </td>
                <td className='col-metric'>
                  <LiftCell
                    canShowLift={Boolean(analytics) && !isControl}
                    isCollectingData={isCollectingData}
                    liftValue={analytics?.lift.cvr ?? 0}
                    formatLift={formatLift}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
